/* eslint-disable @typescript-eslint/prefer-for-of */

import { colors } from "widgets/utils";

import { GROUP_HEADER_HEIGHT } from "../settings.ts";
import { useGenomeViewerStore } from "../store/store.ts";
import type { Track } from "../types.ts";
import { navigateToFeature } from "../utils/navigate-to-feature.ts";
import { navigateToPosition } from "../utils/navigate-to-position.ts";
import { drawCoordinates, drawHorizontalLine, drawVerticalGridLines } from "./graphics/coordinates-renderer.ts";
import { drawGroupHeader } from "./graphics/group-header-renderer.ts";
import {
  drawAlignmentImageBitmaps,
  drawDarkOverlay,
  drawHoveredFeatures,
  drawRangeSelection,
  drawTrackImageBitmaps,
  drawTrackShiftBackground,
} from "./graphics/main-renderer.ts";
import { rangeZoomRenderer } from "./graphics/range-zoom-renderer.ts";
import { drawScrollBars } from "./graphics/scrollbars-renderer.ts";
import { drawTracksHeader } from "./graphics/track-header-renderer.ts";
import { getAllTracksHeight } from "./utils.ts";

let rafId = -1;

/**
 * Use this function when you want to refresh the screen.
 * It will automatically add it to the queue if a rendering is currently already running.
 */
export function refreshMain() {
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(renderMainCanvas);
}

function renderMainCanvas() {
  const state = useGenomeViewerStore.getState();
  const ctx = state.ctx;

  if (!ctx) {
    return;
  }

  if (state.needsRefresh && state.isEveryTrackLoaded()) {
    state.needsRefresh = false;
    // In case of loading a view, we wait for all the data to scroll to Y position
    if (state.positionToNavigate) {
      navigateToPosition(state.positionToNavigate);
      useGenomeViewerStore.setState({
        positionToNavigate: undefined,
      });
    }
    // In case of navigation from feature table, we wait for all the data to go to the feature position and align
    if (state.featureToNavigate) {
      navigateToFeature(state.featureToNavigate, true);
      useGenomeViewerStore.setState({
        featureToNavigate: undefined,
      });
    }
  }

  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Fill the background
  ctx.fillStyle = colors.disabledBackground;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  let offsetY = 0;
  const scrollY = state.scrollY;
  const maxWidth = state.getMaxWidth()[1];
  const originOffset = state.getOriginOffset();

  // Maps maintain insertion order, which could be important for consistent rendering
  // Instead of spreading, iterate directly
  const trackGroupsIterator = state.trackGroups.values();
  let groupResult = trackGroupsIterator.next();

  while (!groupResult.done) {
    const trackGroup = groupResult.value;
    groupResult = trackGroupsIterator.next();

    const graphics = trackGroup.graphics;
    if (!graphics.isVisible) continue;

    // Inline track filtering & mapping for speed
    const tracks: Track[] = [];

    for (let i = 0; i < trackGroup.trackIds.length; ++i) {
      const track = state.tracks.get(trackGroup.trackIds[i]);
      if (track && track.isVisible) tracks.push(track);
    }

    // Precache frequently used properties
    const baseShift = graphics.baseShift || 0;
    const organismLength = graphics.length || 0;
    const contigLength = graphics.length;
    const scrollOffsetY = Math.floor(offsetY - state.scrollY);

    ctx.save();
    ctx.translate(0, scrollOffsetY);

    // Fast sum of heights
    let height = GROUP_HEADER_HEIGHT;

    for (let i = 0; i < tracks.length; ++i) {
      height += tracks[i].height;
    }

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, ctx.canvas.width, height);

    // Only draw visible groups
    if (scrollOffsetY + height > 0 && scrollOffsetY < ctx.canvas.height) {
      drawTrackShiftBackground(baseShift / state.basePerPixel, height, organismLength);
      drawVerticalGridLines(height, baseShift, contigLength);
      drawTrackImageBitmaps(tracks, baseShift);
      drawCoordinates(baseShift, contigLength, maxWidth);
      if (state.showTrackHeaders) {
        drawTracksHeader(tracks);
      }
      drawGroupHeader(`[${trackGroup.organismName}] - ${trackGroup.contigName}`, []);

      if (tracks.length > 0 && state.hoveredGroupId !== tracks[0].groupId && state.asynchronousScrolling) {
        drawDarkOverlay(0, height);
      }
    }

    offsetY += height;
    ctx.restore();
  }

  // Draw the last grid line
  if (offsetY - scrollY > 0 && offsetY - scrollY < ctx.canvas.height) {
    drawHorizontalLine(ctx, offsetY);
  }

  drawAlignmentImageBitmaps();

  if (offsetY - scrollY > 0 && offsetY - scrollY < ctx.canvas.height && state.asynchronousScrolling) {
    drawDarkOverlay(offsetY, ctx.canvas.height);
  }

  if (state.zoomRange) {
    rangeZoomRenderer();
  }

  if (!!state.selectedRange && !!state.ctx) {
    drawRangeSelection(
      state.ctx,
      state.selectedRange,
      state.trackGroups,
      state.tracks,
      state.trackGroupsInSelection,
      state.basePerPixel,
      state.scrollX,
      state.scrollY,
    );
  }

  drawHoveredFeatures();
  drawScrollBars(
    state.ctx!,
    getAllTracksHeight(),
    state.scrollX,
    scrollY,
    maxWidth,
    originOffset,
    state.hoveringHorizontalScrollbar,
    state.hoveringVerticalScrollbar,
  );
}
